import { t } from 'i18next';

import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { PconLoginIdentifier } from '../stores/PconLoginStore';
import { ProjectLoadMethod, ProjectInstance } from '../utils/ProjectInstance';
import { ActionHelper } from '../utils/ActionHelper';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { AppProjectManager } from '../utils/AppProjectManager';
import { SceneStore } from '../stores/SceneStore';
import { SceneActions } from './SceneActions';
import { SaveType, SidebarModule } from '../constants/Enums';
import { ViewSettingsActions } from './ViewSettingsActions';
import { UIHelper } from '../utils/UIHelper';
import { PersistentActions } from './PersistentActions';
import { DialogActions } from './DialogActions';

import utils = egr.wcf.utils;
import { methodWithDebugTimer } from '../submodules/utils/Debug';

// Note: the app api requires a sting as `attributes` but the JS code uses
// this interface
export interface ProjectAttributes extends PconLoginIdentifier {
}

export interface ProjectProperties {
    name?: string;
    mtime: number;
    crtime: number;
    image?: string;
    attributes?: ProjectAttributes;
    loadMethod: ProjectLoadMethod;
}

export abstract class ProjectLoadingError extends utils.CustomError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, ProjectLoadingError.prototype);
    }
}

export class ProjectLoadingCancelError extends ProjectLoadingError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, ProjectLoadingCancelError.prototype);
    }
}

export class UserMissmatchError extends ProjectLoadingError {
    constructor(projectUser: string, currentUser: string) {
        super(
            t(
                // tslint:disable-next-line:max-line-length
                'This project has been created using the user name {{projectUser}}. Now you are logged in as {{currentUser}}. That\'s why the project cannot be opened.',
                {projectUser: projectUser, currentUser: currentUser}
            )
        );
        Object.setPrototypeOf(this, UserMissmatchError.prototype);
    }
}

export class LoginRequiredError extends ProjectLoadingError {
    constructor(projectUser: string) {
        super(
            t(
                'This project has been created using the user name {{projectUser}}. Now you are not logged in. That\'s why the project cannot be opened.',
                {projectUser: projectUser}
            )
        );
        Object.setPrototypeOf(this, LoginRequiredError.prototype);
    }
}

export abstract class ProjectManagerActions {
    public static readonly autoSave: () => Promise<void | null> = ((): () => Promise<void | null> => {
        const autoSaveDelay: number = 30000; // 30 sec
        return ActionHelper.getPausingAction<void>(
            autoSaveDelay,
            ProjectManagerActions.saveTempProject
        );
    })();

    public static removeSelection(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.TOGGLE_PROJECT_SELECTION,
            value: null
        });
    }

    public static toggleProjectSelection(value: string | null, clearOldSelection?: boolean): void {
        if (value != null) {
            AppDispatcher.dispatch({
                actionType: clearOldSelection ? ActionConstants.SET_PROJECT_SELECTION : ActionConstants.TOGGLE_PROJECT_SELECTION,
                value: value
            });
        } else if (clearOldSelection) {
            AppDispatcher.dispatch({
                actionType: ActionConstants.SET_PROJECT_SELECTION,
                value: value
            });
        }
    }

    public static applyFilter(value: string | null): void {
        ProjectManagerActions.removeSelection();
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_PROJECT_FILTER,
            value: utils.string.isNotNullOrEmpty(value) ? value.toLocaleLowerCase() : ''
        });
    }

    public static setCurrentProjectName(value: string): void {
        const project: ProjectInstance | null = ProjectManagerStore.getCurrentProject();
        if (project != null) {
            project.name = value;

            ProjectManagerActions.setLoadedProject(project);
        }
    }

    public static async setProjectName(id: string, name: string): Promise<void> {
        await AppProjectManager.renameProject(id, name);
        return ProjectManagerActions.updateProjectList();
    }

    public static loadProject(id: string, attributes?: ProjectAttributes): Promise<void> {
        return SceneActions.newScene(
            {
                projectId: id,
                projectAttributes: attributes
            },
            SaveType.NOT
        ).then((): void => {
            ViewSettingsActions.changeSidebar(SidebarModule.NONE);
        }).catch((error: Error): void => {
            UIHelper.handleProjectLoadingError(error);
        });
    }

    public static setLoadedProject(project: ProjectInstance): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_LOADED_PROJECT,
            value: project
        });
        PersistentActions.setAutoSaveId(project != null ? project.id : '');
    }

    public static enableEditMode(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.PROJECT_EDITMODE,
            value: true
        });
    }

    public static disableEditMode(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.PROJECT_EDITMODE,
            value: false
        });
    }

    public static resetProjectReleatedSettings(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.RESET_PROJECT_RELATED_SETTINGS,
            value: null
        });
        UIHelper.updateFloor();
    }

    public static duplicateProject(id: string, newName?: string): Promise<void> {
        return AppProjectManager.duplicateProject(id, newName);
    }

    @methodWithDebugTimer('updateProjectList')
    public static async updateProjectList(): Promise<void> {
        const projects: Array<ProjectInstance> = await AppProjectManager.getProjects();

        AppDispatcher.dispatch({
            actionType: ActionConstants.PROJECT_LIST_UPDATE,
            value: projects
        });
    }

    public static deleteProject(id: string): Promise<void> {
        return AppProjectManager.removeProject(id);
    }

    public static duplicateSelectedProject(): void {
        const project: ProjectInstance | null = ProjectManagerStore.getSelectedProject(0);
        if (project != null && utils.string.isNotNullOrEmpty(project.name)) {
            DialogActions.duplicateProject(project.id, project.name);
        }
    }

    public static deleteSelectedProjects(): Promise<void> {
        ViewSettingsActions.closeMessage();

        return ViewSettingsActions.withLock(async (): Promise<void> => {
            await ProjectManagerActions.internalDeleteSelectedProjects();
            await ProjectManagerActions.updateProjectList();
            ProjectManagerActions.removeSelection();
            ProjectManagerActions.disableEditMode();
        });
    }

    protected static async internalDeleteSelectedProjects(): Promise<void> {
        const selectedProjects: Array<string> = ProjectManagerStore.getState().selectedProjects;
        if (selectedProjects != null) {
            for (const projectId of selectedProjects) {
                await AppProjectManager.removeProject(projectId);
            }
        }
    }

    protected static async saveTempProject(): Promise<void> {
        const project: ProjectInstance | null = ProjectManagerStore.getCurrentProject();
        if (project != null && SceneStore.getState().unsavedChanges === true) {
            await AppProjectManager.saveTempProject(project);
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/ProjectManagerActions.ts